import { InMemoryCache, makeVar } from '@apollo/client';
import cloneDeep from 'lodash/cloneDeep';
import { gqlAccount, gqlSortOrderEnum, gqlProfileSearchSortField } from '@minecraft.graphql-types';
import { getDivisionId } from 'lib/localAuthStore';
import { SUBMISSION_TAB } from 'config/constants/submission';
import appConfig from 'config/appConfig';

export type SubmissionTabName = typeof SUBMISSION_TAB[keyof typeof SUBMISSION_TAB];

export interface SubmissionTabState {
  selectedTab: SubmissionTabName;
  roleId: number | string;
  pagination: {
    tab: SubmissionTabName;
    currentPage: number;
    pageLimit: number;
  }[];
}

const submittedSelectedTabStateDefaults: SubmissionTabState = {
  selectedTab: SUBMISSION_TAB.ROSTER,
  roleId: null,
  pagination: [
    {
      tab: SUBMISSION_TAB.ROSTER,
      currentPage: 1,
      pageLimit: appConfig.profileSearchPageSize,
    },
    {
      tab: SUBMISSION_TAB.SELECTED,
      currentPage: 1,
      pageLimit: appConfig.profileSearchPageSize,
    },
    {
      tab: SUBMISSION_TAB.SUBMITTED,
      currentPage: 1,
      pageLimit: appConfig.profileSearchPageSize,
    },
  ],
};

export const rosterSortDefaults = {
  fieldName: gqlProfileSearchSortField.LastName,
  sortOrder: gqlSortOrderEnum.ASC,
};

export const paginationTypeNames = {
  projectList: 'ProjectListPagination',
  invitations: 'InvitationsPagination',
  packages: 'PackagesPagination',
  roster: 'RosterPagination',
  worksheet: 'WorksheetPagination',
  rosterLists: 'RosterListsPagination',
};

export const defaultGlobalPaginations = {
  projectList: {
    pageNumber: 1,
    pageSize: appConfig.projectSearchPageSize,
  },
  invitations: {
    pageNumber: 1,
    pageSize: appConfig.profileSearchPageSize,
  },
  packages: {
    pageNumber: 1,
    pageSize: appConfig.projectSearchPageSize,
  },
  roster: {
    pageNumber: 1,
    pageSize: appConfig.profileSearchPageSize,
  },
  rosterLists: {
    pageNumber: 1,
    pageSize: appConfig.profileSearchPageSize,
  },
  worksheet: {
    pageNumber: 1,
    pageSize: appConfig.pageSizes.length ? 50 : 100,
  },
};

/* Reactive vars for local state */
export const accountDivisionVar = makeVar({
  selectedDivisionId: getDivisionId(),
});

export const submittedSelectedTabState = makeVar(submittedSelectedTabStateDefaults);

export const rosterSortVars = makeVar(rosterSortDefaults);

export const globalPaginationVars = makeVar(defaultGlobalPaginations);

export const cacheConfig = {
  typePolicies: {
    Query: {
      fields: {
        filters: {
          merge: true,
        },
        accountSettings: {
          read() {
            return accountDivisionVar();
          },
        },
        getPresentationFolders: {
          merge(_, incoming = []) {
            return incoming;
          },
        },
        submissionTabs: {
          read() {
            return submittedSelectedTabState();
          },
          merge(existing = submittedSelectedTabStateDefaults, incoming) {
            const { selectedTab, pagination, roleId } = incoming;
            const submissionTabs = { ...existing };

            submissionTabs.selectedTab = selectedTab || submissionTabs.selectedTab;
            submissionTabs.roleId = roleId || submissionTabs.roleId;
            submissionTabs.pagination = pagination;

            return submissionTabs;
          },
        },
        rosterSort: {
          read() {
            return rosterSortVars();
          },
          merge: true,
        },
        globalPaginations: {
          read() {
            return globalPaginationVars();
          },
          merge: true,
        },
      },
    },
    RepWorksheetSubmission: {
      keyFields: ['id', 'role', ['id']],
    },
    Organization: {
      fields: {
        logo: {
          merge: true,
        },
        features: {
          merge(_, incoming = []) {
            return incoming;
          },
        },
      },
    },
  },
};

const cache = new InMemoryCache(cacheConfig);

export default cache;

export interface MockCacheParams {
  submissionTabs?: SubmissionTabState;
  myAccount?: Partial<gqlAccount>;
  accountSettings?: { selectedDivisionId?: number };
}

const mockCacheConfig = ({
  submissionTabs = cloneDeep(submittedSelectedTabStateDefaults),
  myAccount = {},
  accountSettings = {},
}: MockCacheParams) => {
  const mockConfig = cloneDeep(cacheConfig);
  submittedSelectedTabState(submissionTabs);

  (mockConfig.typePolicies.Query.fields as any).submissionTabs = {
    ...(mockConfig.typePolicies.Query.fields as any).submissionTabs,
    read() {
      return submittedSelectedTabState();
    },
  };

  (mockConfig.typePolicies.Query.fields as any).myAccount = {
    read() {
      return myAccount;
    },
  };

  (mockConfig.typePolicies.Query.fields as any).accountSettings = {
    read() {
      return accountSettings;
    },
  };

  // this handles a provider warning in the console
  // that can occur during refetches where the data has changed
  (mockConfig.typePolicies as any).Media = {
    fields: {
      profiles: {
        merge(_, incoming = []) {
          return incoming;
        },
      },
    },
  };

  (mockConfig.typePolicies as any).Profile = {
    fields: {
      credits: {
        merge(_, incoming = []) {
          return incoming;
        },
      },
      skills: {
        merge(_, incoming = []) {
          return incoming;
        },
      },
    },
  };

  (mockConfig.typePolicies as any).Credit = {
    fields: {
      creditMedia: {
        merge(_, incoming = []) {
          return incoming;
        },
      },
    },
  };

  (mockConfig as any).typePolicies.Submission = {
    fields: {
      profile: {
        merge: true,
      },
    },
  };

  return mockConfig;
};

export const mockCache = (params: MockCacheParams = {}) => {
  return new InMemoryCache(mockCacheConfig(params));
};
